import React, { useEffect, useState } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Wallet, CreditCard, Building2, Loader2, ArrowUpRight, CheckCircle2,
  AlertCircle, Users, DollarSign, Calendar, Banknote, Send
} from 'lucide-react';
import { toast } from 'sonner';
import BulkFundWallets from '../../components/BulkFundWallets';

export default function WalletPayrollControls() {
  const [activeTab, setActiveTab] = useState('wallet-payroll');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [walletInfo, setWalletInfo] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);

  // Wallet load
  const [loadAmount, setLoadAmount] = useState('');
  const [loadRef, setLoadRef] = useState('');
  const [loadDesc, setLoadDesc] = useState('');
  const [loadSubmitting, setLoadSubmitting] = useState(false);
  const [loadResult, setLoadResult] = useState(null);

  // Payroll
  const [payrollMonth, setPayrollMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [payrollDesc, setPayrollDesc] = useState('');
  const [payrollSubmitting, setPayrollSubmitting] = useState(false);
  const [payrollResult, setPayrollResult] = useState(null);

  useEffect(() => {
    const loadCompanies = async () => {
      try { setCompanies(await superAdminAPI.getCompanies()); }
      catch { toast.error('Failed to load companies'); }
      finally { setLoading(false); }
    };
    loadCompanies();
  }, []);

  useEffect(() => {
    if (!selectedCompanyId) { setWalletInfo(null); return; }
    const loadWallet = async () => {
      setWalletLoading(true);
      try { setWalletInfo(await superAdminAPI.getCompanyWallet(selectedCompanyId)); }
      catch { setWalletInfo(null); }
      finally { setWalletLoading(false); }
    };
    loadWallet();
  }, [selectedCompanyId]);

  const handleLoadWallet = async (e) => {
    e.preventDefault();
    if (!selectedCompanyId || !loadAmount) return;
    setLoadSubmitting(true);
    setLoadResult(null);
    try {
      const result = await superAdminAPI.loadWallet({
        company_id: selectedCompanyId,
        amount: parseFloat(loadAmount),
        reference: loadRef,
        description: loadDesc,
      });
      setLoadResult(result);
      toast.success(`Loaded ${Number(loadAmount).toLocaleString()} NGN successfully`);
      setLoadAmount('');
      setLoadRef('');
      setLoadDesc('');
      // Refresh wallet info
      const info = await superAdminAPI.getCompanyWallet(selectedCompanyId);
      setWalletInfo(info);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load wallet');
    } finally { setLoadSubmitting(false); }
  };

  const handleDisbursePayroll = async (e) => {
    e.preventDefault();
    if (!selectedCompanyId || !payrollMonth) return;
    setPayrollSubmitting(true);
    setPayrollResult(null);
    try {
      const result = await superAdminAPI.disbursePayroll({
        company_id: selectedCompanyId,
        payroll_month: payrollMonth,
        description: payrollDesc,
      });
      setPayrollResult(result);
      toast.success(`Payroll disbursed: ${result.employees_paid} employees paid`);
      const info = await superAdminAPI.getCompanyWallet(selectedCompanyId);
      setWalletInfo(info);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payroll disbursement failed');
    } finally { setPayrollSubmitting(false); }
  };

  const fmt = (v) => Number(v || 0).toLocaleString('en-NG', { style: 'currency', currency: 'NGN' });

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const tabs = [
    { id: 'wallet-payroll', label: 'Wallet & Payroll', icon: Wallet },
    { id: 'bulk-fund', label: 'Bulk Fund Wallets', icon: Send },
  ];

  return (
    <div className="space-y-6" data-testid="wallet-payroll-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Wallet & Payroll Controls</h1>
        <p className="text-sm text-gray-500">Load company wallets, disburse payroll, and fund employee wallets</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Bulk Fund Tab */}
      {activeTab === 'bulk-fund' && (
        <div data-testid="bulk-fund-tab">
          {/* Company Selector for Bulk Funding */}
          <div className="mb-5">
            <Card className="border-gray-200 shadow-sm">
              <CardContent className="p-4">
                <Label className="text-sm font-medium mb-2 block">Select Company to Fund</Label>
                <Select value={selectedCompanyId || 'none'} onValueChange={v => setSelectedCompanyId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="h-10 border-gray-200" data-testid="fund-company-selector">
                    <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                    <SelectValue placeholder="Choose a company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Choose a company...</SelectItem>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          </div>
          {selectedCompanyId ? (
            <BulkFundWallets
              companyId={selectedCompanyId}
              loadEmployees={(params) => superAdminAPI.getFundingEmployees(selectedCompanyId, params)}
              executeFunding={(data) => superAdminAPI.executeBulkFunding(data)}
            />
          ) : (
            <div className="text-center py-16 text-gray-400">
              <Send className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Select a company above to start funding employee wallets</p>
            </div>
          )}
        </div>
      )}

      {/* Wallet & Payroll Tab */}
      {activeTab === 'wallet-payroll' && (<>

      {/* Company Selector */}
      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-4">
          <Label className="text-sm font-medium mb-2 block">Select Company</Label>
          <Select value={selectedCompanyId || 'none'} onValueChange={v => { setSelectedCompanyId(v === 'none' ? '' : v); setLoadResult(null); setPayrollResult(null); }}>
            <SelectTrigger className="h-10 border-gray-200" data-testid="company-selector">
              <Building2 className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Choose a company" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Choose a company...</SelectItem>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedCompanyId && (
        <>
          {/* Wallet Overview */}
          {walletLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>
          ) : walletInfo && (
            <div className="grid sm:grid-cols-3 gap-4">
              <Card className="border-gray-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-4 h-4 text-pink-500" />
                    <span className="text-xs text-gray-500">Wallet Balance</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900" data-testid="company-balance">{fmt(walletInfo.wallet?.balance)}</p>
                </CardContent>
              </Card>
              <Card className="border-gray-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    <span className="text-xs text-gray-500">Monthly Payroll</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900" data-testid="company-payroll">{fmt(walletInfo.monthly_payroll)}</p>
                </CardContent>
              </Card>
              <Card className="border-gray-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs text-gray-500">Active Employees</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900" data-testid="company-employees">{walletInfo.employees}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Two-column: Load Wallet | Disburse Payroll */}
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Load Wallet */}
            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-emerald-500" /> Load Company Wallet
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLoadWallet} className="space-y-3">
                  <div>
                    <Label className="text-xs">Amount (NGN)</Label>
                    <Input type="number" value={loadAmount} onChange={e => setLoadAmount(e.target.value)}
                      required min="1" step="0.01" placeholder="1,000,000" className="h-9" data-testid="load-amount" />
                  </div>
                  <div>
                    <Label className="text-xs">Reference (optional)</Label>
                    <Input value={loadRef} onChange={e => setLoadRef(e.target.value)} placeholder="BANK-TXN-001" className="h-9" data-testid="load-reference" />
                  </div>
                  <div>
                    <Label className="text-xs">Description (optional)</Label>
                    <Input value={loadDesc} onChange={e => setLoadDesc(e.target.value)} placeholder="March funding" className="h-9" />
                  </div>
                  <Button type="submit" disabled={loadSubmitting || !loadAmount} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="load-wallet-btn">
                    {loadSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowUpRight className="w-4 h-4 mr-1" /> Load Wallet</>}
                  </Button>
                </form>

                {loadResult && (
                  <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-100" data-testid="load-result">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-700">Wallet Loaded</span>
                    </div>
                    <div className="text-xs text-emerald-600 space-y-1">
                      <p>Amount: {fmt(loadResult.amount)}</p>
                      <p>New Balance: {fmt(loadResult.new_balance)}</p>
                      <p>Ref: {loadResult.reference}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Disburse Payroll */}
            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-pink-500" /> Disburse Payroll
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleDisbursePayroll} className="space-y-3">
                  <div>
                    <Label className="text-xs">Payroll Month</Label>
                    <Input type="month" value={payrollMonth} onChange={e => setPayrollMonth(e.target.value)}
                      required className="h-9" data-testid="payroll-month" />
                  </div>
                  <div>
                    <Label className="text-xs">Description (optional)</Label>
                    <Input value={payrollDesc} onChange={e => setPayrollDesc(e.target.value)} placeholder="March 2026 payroll" className="h-9" />
                  </div>

                  {walletInfo && (
                    <div className="p-2.5 rounded-lg bg-gray-50 text-xs text-gray-600 space-y-1">
                      <div className="flex justify-between"><span>Employees:</span><span className="font-medium">{walletInfo.employees}</span></div>
                      <div className="flex justify-between"><span>Est. Total:</span><span className="font-medium">{fmt(walletInfo.monthly_payroll)}</span></div>
                      <div className="flex justify-between"><span>Wallet:</span><span className="font-medium">{fmt(walletInfo.wallet?.balance)}</span></div>
                      {walletInfo.wallet?.balance < walletInfo.monthly_payroll && (
                        <div className="flex items-center gap-1 text-red-500 mt-1">
                          <AlertCircle className="w-3 h-3" /> Insufficient balance
                        </div>
                      )}
                    </div>
                  )}

                  <Button type="submit" disabled={payrollSubmitting} className="w-full bg-pink-500 hover:bg-pink-600 text-white" data-testid="disburse-payroll-btn">
                    {payrollSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1" /> Disburse Payroll</>}
                  </Button>
                </form>

                {payrollResult && (
                  <div className="mt-4 p-3 rounded-lg bg-pink-50 border border-pink-100" data-testid="payroll-result">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-pink-600" />
                      <span className="text-sm font-medium text-pink-700">Payroll Disbursed</span>
                    </div>
                    <div className="text-xs text-pink-600 space-y-1">
                      <p>Month: {payrollResult.month}</p>
                      <p>Employees Paid: {payrollResult.employees_paid}</p>
                      <p>Total: {fmt(payrollResult.total_amount)}</p>
                      <p>Ref: {payrollResult.reference}</p>
                      <p>Remaining Balance: {fmt(payrollResult.company_wallet_balance)}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
      </>)}
    </div>
  );
}
